import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { cn } from '@/lib/utils/cn';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import { useCreditsHistory } from '../api';
import type { CreditsHistoryRow } from '../types';

/* ---- Wire helpers ---- */

const num = (v: number | string | undefined | null): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString();
};

const txt = (v: string | number | undefined | null): string => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

/**
 * Action semantics — drives both the credits amount color/sign and the
 * Action label color. The wire values observed in staging include:
 *
 *   addition, tts_addition                      → green ("+")
 *   deduction, tts_deduction, campaign_deduction→ red   ("−")
 *   tts_static                                  → neutral
 *
 * Match by suffix so any new `*_addition` / `*_deduction` flavor classifies
 * correctly without a code change. The legacy `add` / `remove` literals are
 * still recognized for backwards compatibility.
 */
function classifyAction(action?: string): 'add' | 'deduct' | 'neutral' {
  if (!action) return 'neutral';
  const a = action.toLowerCase();
  if (a === 'add' || a === 'addition' || a.endsWith('_addition')) return 'add';
  if (
    a === 'remove' ||
    a === 'deduct' ||
    a === 'deduction' ||
    a.endsWith('_deduction')
  )
    return 'deduct';
  return 'neutral';
}

const ADD_CLASS = 'text-emerald-600 dark:text-emerald-400';
const DEDUCT_CLASS = 'text-rose-600 dark:text-rose-400';

/** Render the credits amount in green for additions, red for deductions. */
function CreditsCell({
  amount,
  action,
}: {
  amount: number | undefined;
  action?: string;
}) {
  const kind = classifyAction(action);
  const value = Number(amount ?? 0);
  return (
    <span
      className={cn(
        'tabular-nums font-medium',
        kind === 'add' && ADD_CLASS,
        kind === 'deduct' && DEDUCT_CLASS,
      )}
    >
      {kind === 'add' ? '+' : kind === 'deduct' ? '−' : ''}₹{value.toLocaleString()}
    </span>
  );
}

/** Action label, colored the same way as the credits amount. */
function ActionCell({ action }: { action?: string }) {
  if (!action) return <>—</>;
  const kind = classifyAction(action);
  return (
    <span
      className={cn(
        'font-medium capitalize',
        kind === 'add' && ADD_CLASS,
        kind === 'deduct' && DEDUCT_CLASS,
      )}
    >
      {action.replace(/_/g, ' ')}
    </span>
  );
}

/* ---- Page ---- */

export function CreditsHistoryPage() {
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const isReseller = role === USER_ROLES.ADMIN;
  const isEndUser = role === USER_ROLES.USER;

  // Admin tiers default to "today"; end-user / reseller default to unbounded.
  const today = new Date();
  const [startDraft, setStartDraft] = useState<Date | undefined>(
    isSubOrSuper ? today : undefined,
  );
  const [endDraft, setEndDraft] = useState<Date | undefined>(
    isSubOrSuper ? today : undefined,
  );
  const [start, setStart] = useState(isSubOrSuper ? formatDate(today) : '');
  const [end, setEnd] = useState(isSubOrSuper ? formatDate(today) : '');

  const query = useCreditsHistory({ startDate: start, endDate: end });
  const rows = query.data ?? [];

  /** SADMIN row-action: trigger campaign_deduction report generation. */
  const generateReport = useMutation({
    mutationFn: (campaignId: number) =>
      api.post<{ message?: string }>('/api/obd/report/generate', {
        campaignId,
        reportType: 'campaign_deduction',
        userId: Number(userId),
      }),
    onSuccess: (res) => toast.success(res?.message ?? 'Report queued'),
    onError: () => toast.error('Could not start the report.'),
  });

  const applyFilter = () => {
    setStart(startDraft ? formatDate(startDraft) : '');
    setEnd(endDraft ? formatDate(endDraft) : '');
  };

  const clearFilter = () => {
    setStartDraft(undefined);
    setEndDraft(undefined);
    setStart('');
    setEnd('');
  };

  const columns = useMemo<ColumnDef<CreditsHistoryRow>[]>(() => {
    // For campaign-deduction rows the legacy UI substitutes the "From"
    // column with the literal "Campaign" — preserving that.
    const fromLabel = (r: CreditsHistoryRow): string =>
      r.action === 'campaign_deduction' ? 'Campaign' : txt(r.fromUsername);

    if (isSubOrSuper) {
      return [
        {
          accessorKey: 'campId',
          header: 'Campaign Id',
          size: 110,
          cell: ({ row }) => (
            <span className="font-mono text-xs text-muted-foreground">
              {txt(row.original.campId)}
            </span>
          ),
        },
        {
          accessorKey: 'userId',
          header: 'User Id',
          size: 90,
          cell: ({ row }) => (
            <span className="font-mono text-xs">{txt(row.original.userId)}</span>
          ),
        },
        {
          accessorKey: 'fromUsername',
          header: 'From',
          cell: ({ row }) => fromLabel(row.original),
        },
        {
          accessorKey: 'toUsername',
          header: 'To',
          cell: ({ row }) => txt(row.original.toUsername),
        },
        {
          accessorKey: 'actionOn',
          header: 'Action On',
          cell: ({ row }) => txt(row.original.actionOn),
        },
        {
          accessorKey: 'credits',
          header: 'Amount',
          size: 120,
          cell: ({ row }) => (
            <CreditsCell amount={row.original.credits} action={row.original.action} />
          ),
        },
        {
          accessorKey: 'campaignName',
          header: 'Campaign',
          cell: ({ row }) => txt(row.original.campaignName),
        },
        {
          accessorKey: 'action',
          header: 'Action',
          size: 130,
          cell: ({ row }) => <ActionCell action={row.original.action} />,
        },
        {
          accessorKey: 'userBalance',
          header: 'Balance',
          size: 110,
          cell: ({ row }) => (
            <span className="tabular-nums">{num(row.original.userBalance)}</span>
          ),
        },
        {
          accessorKey: 'reqDate',
          header: 'Date',
          size: 180,
          cell: ({ row }) => formatDateTime(row.original.reqDate),
        },
        {
          accessorKey: 'bd',
          header: 'BD',
          size: 90,
          cell: ({ row }) => (
            <span className="tabular-nums">{num(row.original.bd)}</span>
          ),
        },
        {
          accessorKey: 'abd',
          header: 'ABD',
          size: 90,
          cell: ({ row }) => (
            <span className="tabular-nums">{num(row.original.abd)}</span>
          ),
        },
        {
          accessorKey: 'ttsCredits',
          header: 'TTS Credits',
          size: 110,
          cell: ({ row }) => (
            <span className="tabular-nums">{num(row.original.ttsCredits)}</span>
          ),
        },
        {
          id: 'downloadReport',
          header: 'Action',
          size: 110,
          enableSorting: false,
          cell: ({ row }) => {
            if (row.original.action !== 'campaign_deduction') return '—';
            const campId = row.original.campId;
            if (!campId) return '—';
            return (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => generateReport.mutate(campId)}
                disabled={generateReport.isPending}
                title="Download Report"
                aria-label="Download Report"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            );
          },
        },
      ];
    }

    if (isReseller) {
      // Legacy reseller view: shows audit-style columns but no campId / BD /
      // ABD / report-download. `userBalance` lives in the model (legacy column
      // declared `hidden: true`) — we expose it as a togglable column.
      return [
        {
          accessorKey: 'userId',
          header: 'User Id',
          size: 90,
          cell: ({ row }) => (
            <span className="font-mono text-xs">{txt(row.original.userId)}</span>
          ),
        },
        {
          accessorKey: 'fromUsername',
          header: 'From User',
          // Legacy: for campaign_deduction rows From/To were both replaced
          // with the userId so the reseller knows which user the deduction
          // belongs to.
          cell: ({ row }) =>
            row.original.action === 'campaign_deduction'
              ? txt(row.original.userId)
              : txt(row.original.fromUsername),
        },
        {
          accessorKey: 'toUsername',
          header: 'To User',
          cell: ({ row }) =>
            row.original.action === 'campaign_deduction'
              ? txt(row.original.userId)
              : txt(row.original.toUsername),
        },
        {
          accessorKey: 'actionOn',
          header: 'Action On',
          cell: ({ row }) => txt(row.original.actionOn),
        },
        {
          accessorKey: 'credits',
          header: 'Amount',
          size: 120,
          cell: ({ row }) => (
            <CreditsCell amount={row.original.credits} action={row.original.action} />
          ),
        },
        {
          accessorKey: 'campaignName',
          header: 'Campaign',
          cell: ({ row }) => txt(row.original.campaignName),
        },
        {
          accessorKey: 'action',
          header: 'Action',
          size: 130,
          cell: ({ row }) => <ActionCell action={row.original.action} />,
        },
        {
          accessorKey: 'userBalance',
          header: 'Balance',
          size: 110,
          cell: ({ row }) => (
            <span className="tabular-nums">{num(row.original.userBalance)}</span>
          ),
        },
        {
          accessorKey: 'ttsCredits',
          header: 'TTS Credits',
          size: 110,
          cell: ({ row }) => (
            <span className="tabular-nums">{num(row.original.ttsCredits)}</span>
          ),
        },
        {
          accessorKey: 'reqDate',
          header: 'Date',
          size: 180,
          cell: ({ row }) => formatDateTime(row.original.reqDate),
        },
      ];
    }

    // ROLE_USER (and fallback): user-facing view — no IDs, no balance, just
    // the credits movement.
    return [
      {
        accessorKey: 'credits',
        header: 'Amount',
        size: 120,
        cell: ({ row }) => (
          <CreditsCell amount={row.original.credits} action={row.original.action} />
        ),
      },
      {
        accessorKey: 'campaignName',
        header: 'Campaign',
        cell: ({ row }) => txt(row.original.campaignName),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 130,
        cell: ({ row }) => <ActionCell action={row.original.action} />,
      },
      {
        accessorKey: 'reqDate',
        header: 'Date',
        size: 180,
        cell: ({ row }) => formatDateTime(row.original.reqDate),
      },
      {
        accessorKey: 'ttsCredits',
        header: 'TTS Credits',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">{num(row.original.ttsCredits)}</span>
        ),
      },
    ];
  }, [isSubOrSuper, isReseller, generateReport]);

  // For the reseller view, `userBalance` was hidden by default in legacy —
  // start with it collapsed but still toggleable from the Columns menu.
  const initialVisibility = isReseller ? { userBalance: false } : undefined;

  return (
    <FillPage>
      <PageHeader
        title="Credit Ledger"
        description={
          isEndUser
            ? 'Your personal record of credit movements.'
            : 'Audit log of voice credit movements.'
        }
      />

      <DataTable<CreditsHistoryRow, unknown>
        tableId={`users:credits-history:${isSubOrSuper ? 'sadmin' : isReseller ? 'admin' : 'user'}:v7`}
        beforeTable={
          <div className="flex flex-wrap items-end gap-2 rounded-md border bg-card px-3 py-2.5">
            <div className="space-y-1">
              <label className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                From
              </label>
              <DatePicker value={startDraft ?? null} onChange={setStartDraft} className="w-44" />
            </div>
            <div className="space-y-1">
              <label className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                To
              </label>
              <DatePicker value={endDraft ?? null} onChange={setEndDraft} className="w-44" />
            </div>
            <Button onClick={applyFilter} className="ml-auto">
              <Search className="h-4 w-4" />
              Search
            </Button>
            <Button variant="ghost" onClick={clearFilter}>
              Clear
            </Button>
          </div>
        }
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search…"
        emptyTitle="No credit movements"
        emptyDescription={
          start || end
            ? 'No entries in the selected date range.'
            : 'Pick a date range and search to see results.'
        }
        getRowId={(row, idx) =>
          row.chId !== undefined ? String(row.chId) : `row-${idx}`
        }
        initialColumnVisibility={initialVisibility}
        exportFilename="credits-history.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
